import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { Card, EmptyState, Spinner, Tag, fmtDateTime } from '../../lib/ui.tsx';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';

interface QueueTake {
  id: string;
  submittedAt: string;
  accuracyScore: number | null;
  durationSec: number;
  student?: { id: string; name: string };
  exercise?: { id: string; name: string };
}

/** D3 · One inbox across all students, oldest first — a coffee-break task. */
export default function ReviewQueue() {
  const { data, isLoading } = useQuery({
    queryKey: ['review-queue'],
    queryFn: () => api<QueueTake[]>('/api/review-queue'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Review queue</h1>
        {data && data.length > 0 && <Tag tone="brand">{data.length} waiting</Tag>}
      </div>
      {!data || data.length === 0 ? (
        <EmptyState title="Inbox zero 🎉" hint="New submissions land here, oldest first." />
      ) : (
        <div className="space-y-2">
          {data.map((take, i) => (
            <Link key={take.id} to={`/coach/review/${take.id}`} className="block">
              <Card className={i === 0 ? 'border-brand-300' : ''}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                      {take.student?.name} · {exLabel(take.exercise)}
                    </p>
                    <p className="text-xs text-slate-500">
                      submitted {fmtDateTime(take.submittedAt)} · {Math.round(take.durationSec)}s
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {take.accuracyScore !== null && (
                      <Tag tone={take.accuracyScore >= 80 ? 'green' : take.accuracyScore >= 60 ? 'amber' : 'red'}>
                        {take.accuracyScore}
                      </Tag>
                    )}
                    <span className="text-brand-600">→</span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
